import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "../css/Dashboard.css";

import HourlyChatTrafficChart from "../components/DashboardCharts/HourlyChatTrafficChart";
import HourlyActiveUsersChart from "../components/DashboardCharts/HourlyActiveUsersChart";
import UsageOverTimeChart from "../components/DashboardCharts/UsageOverTimeChart";
import TokenUsageChart from "../components/DashboardCharts/TokenUsageChart";
import TotalActiveUsersChart from "../components/DashboardCharts/TotalActiveUsersChart";
import UserPresenceMap from "../components/DashboardCharts/UserPresenceMap";
import ModelUsageChart from "../components/DashboardCharts/ModelUsageChart";
import BotPerformance from "../components/DashboardCharts/BotPerformance";
import Notifications from "../components/DashboardCharts/Notifications";
import { useState, useEffect } from "react";
import api from "../api";
import DisclaimerPopup from "../auth/DisclaimerPopup";
import { showErrorToast, showSuccessToast } from "../utils/toastUtils";

import TotalEnterprise from "../assets/AdminIcon 2/TotalEnterprize.svg";
import TotalUser from "../assets/AdminIcon 2/TotalUser.svg";
import TotalModels from "../assets/AdminIcon 2/TotalModelsIconBlack.svg";
import TotalTrainedDocuments from "../assets/AdminIcon 2/TotalDocument.svg";
import TotalDocumentSize from "../assets/AdminIcon 2/TotalDcumentSize.svg";
import TotalChats from "../assets/AdminIcon 2/TotalChat.svg";
import Notification from "../assets/AdminIcon 2/TotalNotification.svg";
import TotalSystemAlert from "../assets/icons/DashboardsIcons/TotalSystemAlert.png";
import TotalFlaggedMessages from "../assets/icons/DashboardsIcons/TotalFlaggedMessages.png";
import TotalAbandonment from "../assets/icons/DashboardsIcons/TotalAbandonment.png";
import TotalCountries from "../assets/AdminIcon 2/TotalCountries.svg";
import AverageSessionTime from "../assets/AdminIcon 2/TotalSessionTime.svg";
import TBD from "../assets/icons/DashboardsIcons/TBD.png";
import TBDIcon from "../assets/icons/DashboardsIcons/TBD_Icon.png";

const getIcon = (type) => {
  switch (type) {
    case "totalEnterprise":
      return TotalEnterprise;
    case "totaluser":
      return TotalUser;
    case "totalmodels":
      return TotalModels;
    case "totaltraineddocuments":
      return TotalTrainedDocuments;
    case "totaldocumentsize":
      return TotalDocumentSize;
    case "totalchats":
      return TotalChats;
    case "totalnotifications":
      return Notification;
    case "totalsystemalert":
      return TotalSystemAlert;
    case "totalflaggedmessages":
      return TotalFlaggedMessages;
    case "totalabandonment":
      return TotalAbandonment;
    case "totalcountries":
      return TotalCountries;
    case "averagesessiontime":
      return AverageSessionTime;
    case "tbd":
      return TBD;
    default:
      return TBDIcon;
  }
};

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalEnterprises: 0,
    activeEnterprises: 0,
    totalUsers: 0,
    activeUsers: 0,
    totalModels: 0,
    activeModels: 0,
    totalChats: 0,
  });
  const [userName, setUserName] = useState("User");
  const [showDisclaimer, setShowDisclaimer] = useState(false);

  useEffect(() => {
    const user = JSON.parse(
      localStorage.getItem("user") || sessionStorage.getItem("user")
    );
    if (user && user.user_name) {
      setUserName(user.user_name);
    }

    // 👇 Show disclaimer if terms not accepted
    if (user && !user.termsAccepted) {
      setShowDisclaimer(true);
    }
  }, []);

  const token =
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token");

  const fetchStats = async () => {
    if (!token || showDisclaimer) return;

    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };

    try {
      const baseUrl = process.env.REACT_APP_API_URL;

      const [
        enterpriseRes,
        userRes,
        modelRes,
        chatRes,
        documentRes,
        notificationRes,
        presenceRes, // 👈 Added user presence API
      ] = await Promise.all([
        api.get(`${baseUrl}/enterprises/counts/all`, config),
        api.get(`${baseUrl}/users/count/all`, config),
        api.get(`${baseUrl}/models1/status-summary`, config),
        api.get(`${baseUrl}/chat/total-conversations`, config),
        api.get(`${baseUrl}/uploaded-files/stats/summary`, config),
        api.get(`${baseUrl}/uploaded-files/stats/notification`, config),
        api.get(`${baseUrl}/auth/user-presence`, config), // 👈 Added here
      ]);

      setStats({
        totalEnterprises: enterpriseRes.data.total,
        activeEnterprises: enterpriseRes.data.active,
        totalUsers: userRes.data.totalUsers,
        activeUsers: userRes.data.activeUsers,
        totalModels: modelRes.data.total,
        activeModels: modelRes.data.active,
        totalChats: chatRes.data.totalConversations,
        totalDocuments: documentRes.data.totalDocuments, // 👈
        totalDocumentSize: documentRes.data.totalSizeMB, // 👈 in MB
        totalNotifications: notificationRes.data.totalNotifications, // 👈
        avrageSessionTime: chatRes.data.averageSessionTime, // 👈
        totalCountries: presenceRes.data.total_countries, // 👈 Added here
      });
    } catch (err) {
      console.error("❌ Error fetching stats", err);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [showDisclaimer]);
  const handleDisclaimerAccept = async () => {
    const user = JSON.parse(
      localStorage.getItem("user") || sessionStorage.getItem("user")
    );

    try {
      await api.post(
        `${process.env.REACT_APP_API_URL}/users/accept-terms`,
        { accepted: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const updatedUser = { ...user, termsAccepted: true };
      if (localStorage.getItem("user")) {
        localStorage.setItem("user", JSON.stringify(updatedUser));
      } else {
        sessionStorage.setItem("user", JSON.stringify(updatedUser));
      }

      setShowDisclaimer(false);

      // ✅ Add this line:
      showSuccessToast("Terms accepted successfully.");
    } catch (error) {
      console.error("❌ Failed to accept disclaimer", error);
      showErrorToast("Failed to accept terms. Please try again.");
    }
  };

  const cards = [
    {
      label: "Total Enterprizes",
      total: stats.totalEnterprises,
      active: stats.activeEnterprises,
      icon: "totalEnterprise",
    },
    {
      label: "Total Users",
      total: stats.totalUsers,
      active: stats.activeUsers,
      icon: "totaluser",
    },
    {
      label: "Total Models",
      total: stats.totalModels,
      active: stats.activeModels,
      icon: "totalmodels",
    },
    {
      label: "Total Trained Documents",
      value: stats.totalDocuments ?? 0,
      icon: "totaltraineddocuments",
    },
    {
      label: "Total Documents Size",
      value: `${stats.totalDocumentSize ?? 0} MB`,
      icon: "totaldocumentsize",
    },

    {
      label: "Total Conversations",
      total: stats.totalChats,
      active: null,
      icon: "totalchats",
    },
    {
      label: "Total Notifications",
      value: stats.totalNotifications ?? 0,
      icon: "totalnotifications",
    },
    // { label: "Total System Alert", value: 0, icon: "totalsystemalert" },
    // { label: "Total Flagged Messages", value: 0, icon: "totalflaggedmessages" },
    // { label: "Total Abandonment", value: 0, icon: "totalabandonment" },
    {
      label: "Total Countries",
      value: stats.totalCountries || 0,
      icon: "totalcountries",
    },
    {
      label: "Average Session Time",
      value: stats.avrageSessionTime ?? 0,
      icon: "averagesessiontime",
    },
    // { label: "TBD", value: 0, icon: "tbd" },
    // { label: "TBD", value: 0, icon: "tbd" },
    // { label: "TBD", value: 0, icon: "tbd" },
  ];

  return (
    <>
      {showDisclaimer && <DisclaimerPopup onAccept={handleDisclaimerAccept} />}
      <div className="m-2 overflow-y-auto overflow-x-hidden">
        <div className="p-2">
          <h5
            className="mt-2 refresh-prompts mb-4"
            style={{
              fontSize: "18px",
              fontWeight: 600,
              lineHeight: "40px",
            }}
          >
            Greetings{" "}
            {userName
              ? userName.charAt(0).toUpperCase() + userName.slice(1)
              : "-"}
            . Your research hub is ready.
          </h5>

          {/* STATS CARDS */}
          <div className="stats-container gap-3 mt-0">
            {cards.map((stat, index) => (
              <div key={index} className="dashboard-box p-3 rounded shadow-sm">
                <div className="d-flex align-items-center mb-2">
                  <img
                    src={getIcon(stat.icon)}
                    alt={stat.label}
                    style={{
                      width: "22px",
                      height: "22px",
                    }}
                    className="icon-image1"
                  />
                  <h6 className="ms-2 mb-0 text-nowrap">{stat.label}</h6>
                </div>
                <h2 className="fw-bold">
                  {stat.total !== undefined ? stat.total : stat.value}
                </h2>
                <div className="box-summary">
                  {stat.active !== undefined && stat.active !== null ? (
                    <p className="mb-0">Active: {stat.active}</p>
                  ) : (
                    <p className="mb-0" style={{ visibility: "hidden" }}>
                      Active: 0
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* CHARTS */}
          <div className="row g-4">
            <div className="col-md-6">
              <div className="h-100">
                <HourlyChatTrafficChart />
              </div>
            </div>

            <div className="col-md-6">
              <div className="h-100">
                <HourlyActiveUsersChart />
              </div>
            </div>

            <div className="col-md-6">
              <div className="h-100">
                <UsageOverTimeChart />
              </div>
            </div>

            <div className="col-md-6">
              <div className="h-100">
                <TokenUsageChart />
              </div>
            </div>

            <div className="col-md-6">
              <div className="h-100">
                <TotalActiveUsersChart />
              </div>
            </div>

            <div className="col-md-6">
              <div className="chart-card rounded h-100">
                <UserPresenceMap />
              </div>
            </div>

            <div className="row g-2 p-2">
              <div className="col-md-4">
                <div className="chart-card1 h-100">
                  <ModelUsageChart />
                </div>
              </div>

              <div className="col-md-4">
                <div className="chart-card1 h-100">
                  <BotPerformance />
                </div>
              </div>

              <div className="col-md-4">
                <div className="chart-card1 h-100">
                  <Notifications />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Dashboard;
